import { rpaBridge } from '@/tauri/rpa';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { agentHarnessClient } from '@heidi/agents';
import { agentHarnessKeys } from '@heidi/agents/contracts';
import { sanitizeError } from '@heidi/runtime/logger';
import pTimeout from 'p-timeout';

const cancelComputerUseRunSettled = async (agentRunId: string): Promise<void> => {
  const cancel = agentHarnessClient
    .cancelTurn(agentRunId)
    .then(() => {
      void reactQueryClient.invalidateQueries({
        queryKey: agentHarnessKeys.run(agentRunId),
      });
    })
    .catch((error: unknown) => {
      logger.warn('[rpa] cancel computer-use run failed', {
        agent_run_id: agentRunId,
        error: error instanceof Error ? error.message : String(error),
      });
    });
  const stop = rpaBridge.isAvailable()
    ? rpaBridge.stopRunExecutorFor(agentRunId).catch((error: unknown) => {
        // Latency bonus only — BE cancel is source of truth — but ACL rejects
        // (missing allow-stop-run-executor-for) must not stay silent.
        logger.warn('[rpa] stop run executor for cancel failed', {
          agent_run_id: agentRunId,
          error: error instanceof Error ? error.message : String(error),
        });
      })
    : undefined;
  await Promise.all([cancel, stop]);
};

/**
 * Cancel a computer-use run. On the cutover path, pass the CHILD run id: the BE
 * posts the cancelled tool result on the parent stream and the parent turn stays
 * alive to react — never cancel the parent to stop a desktop task.
 *
 * BE is the source of truth: cancel the turn server-side and let derived run
 * state confirm it. The run-owned executor stop is a latency bonus only — the
 * executor also stops when the child stream ends. Never starts a new conversation.
 */
export const cancelComputerUseRun = (agentRunId: string): void => {
  void cancelComputerUseRunSettled(agentRunId);
};

export const COMPUTER_USE_LOGOUT_STOP_TIMEOUT_MS = 10_000;

const stopComputerUseForSignedOutMachine = async (): Promise<void> => {
  await rpaBridge.desktopTargetStop().catch((error: unknown) => {
    logger.warn('[rpa] logout desktop-target stop failed', {
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
  });
  const snapshot = await rpaBridge.readRunExecutorState().catch(() => null);
  const cancel =
    snapshot && snapshot.status?.state !== 'stopped'
      ? cancelComputerUseRunSettled(snapshot.agentRunId)
      : undefined;
  const stop = rpaBridge.stopRunExecutor().catch((error: unknown) => {
    logger.warn('[rpa] logout executor stop failed', {
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
  });
  await Promise.all([cancel, stop]);
};

export const stopComputerUseOnLogout = async (): Promise<void> => {
  if (!rpaBridge.isAvailable()) return;
  try {
    await pTimeout(stopComputerUseForSignedOutMachine(), {
      milliseconds: COMPUTER_USE_LOGOUT_STOP_TIMEOUT_MS,
    });
  } catch (error: unknown) {
    logger.warn('[rpa] logout computer-use stop failed', {
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
  }
};
